const post = {
  "slug": "checkout-dm-referral-program-earn-free-days",
  "title": "How the Checkout DM Referral Program Works",
  "seoTitle": "Checkout DM Referral Program: Earn Free Days",
  "description": "Invite another Instagram seller to Checkout DM and earn 10 extra days on your plan when they subscribe. What counts, what does not and common questions.",
  "excerpt": "Every merchant you bring who pays for a plan adds 10 days to yours. Here is how to get your link, what counts as a successful referral and the rules worth knowing.",
  "category": "Product",
  "tags": [
    "Checkout DM referral",
    "Free plan days",
    "Refer and earn",
    "Instagram sellers"
  ],
  "date": "2026-11-13",
  "cover": {
    "src": "/blog-images/checkout-dm-referral-program-earn-free-days-cover.png",
    "alt": "An invite link being shared and a plan extended by 10 days when a referred merchant subscribes"
  },
  "blocks": [
    {
      "type": "p",
      "text": "If another Instagram seller joins Checkout DM through your link and subscribes to a paid plan, 10 days are added to your current plan. There are no codes to redeem and no forms to fill in. It is applied automatically. This guide covers how to get your link, what earns days, what does not and the rules that catch people out."
    },
    {
      "type": "h2",
      "text": "How it works in three steps"
    },
    {
      "type": "ol",
      "items": [
        "**Share your link.** Copy your personal invite link from Billing, under Refer and earn, and send it to another Instagram seller.",
        "**They sign up.** They connect their Instagram through your link, and their invite is saved automatically.",
        "**They subscribe, you earn.** When their first payment goes through, 10 days are added to your plan and you get a notification."
      ]
    },
    {
      "type": "p",
      "text": "Signing up alone earns nothing. The days arrive the moment the merchant you invited pays for their first plan."
    },
    {
      "type": "h2",
      "text": "What the days are worth"
    },
    {
      "type": "p",
      "text": "The reward is always 10 days of your own plan, per referral, whether you pay monthly or yearly. What changes is the value of those days, because a day of Pro costs more than a day of Starter. At current monthly prices, the approximate values are:"
    },
    {
      "type": "table",
      "headers": [
        "Your plan",
        "Monthly price",
        "1 referral (10 days)",
        "3 referrals (30 days)"
      ],
      "rows": [
        [
          "Creator",
          "\u20b9499",
          "about \u20b9170",
          "about \u20b9500"
        ],
        [
          "Starter",
          "\u20b91,499",
          "about \u20b9500",
          "about \u20b91,500"
        ],
        [
          "Growth",
          "\u20b92,899",
          "about \u20b91,000",
          "about \u20b92,900"
        ],
        [
          "Pro",
          "\u20b96,499",
          "about \u20b92,200",
          "about \u20b96,500"
        ]
      ]
    },
    {
      "type": "p",
      "text": "These values are estimates to show scale, not cash. The reward is extra days on your plan. There is no cap, so 3 successful referrals give you 30 days, 6 give you 60 and so on. On a yearly plan a day costs a little less than on a monthly one, so the value is somewhat lower than the table, but it is still 10 days. If you want the full picture of what each plan costs, see our guide to [Checkout DM pricing and plans](/blog/checkout-dm-pricing-and-plans-explained)."
    },
    {
      "type": "h2",
      "text": "What counts and what does not"
    },
    {
      "type": "p",
      "text": "A referral counts when all three of these are true: a new merchant signs up through your link, they then pay for any paid plan (monthly or yearly), and your own paid plan is active at that moment. These do not count:"
    },
    {
      "type": "ul",
      "items": [
        "Sign-ups that stay on the Free plan.",
        "Merchants who already paid Checkout DM before.",
        "Your own account, or an account using the same email as yours.",
        "Zero-amount payments, for example a 100%-off coupon. Only real money received counts."
      ]
    },
    {
      "type": "callout",
      "tone": "peach",
      "title": "Keep your own plan active",
      "text": "Days are only added to an active paid plan. If your plan has already expired when your friend pays, that referral does not earn days. Renew before you share your link widely."
    },
    {
      "type": "h2",
      "text": "How long your link stays valid"
    },
    {
      "type": "p",
      "text": "When someone opens your link, their browser remembers your invite for 90 days. If they sign up through Instagram within that time, on the same browser and device, your invite is saved. If they wait longer, clear their browser data or sign up on a different device or browser, the invite can be lost. If a friend\u2019s invite was not recorded, contact Checkout DM with their Instagram username and the team will check it."
    },
    {
      "type": "p",
      "text": "A friend who starts on the Free plan and upgrades later still earns you the days, as long as their account still exists. Free accounts with no activity for 30 days are deleted, and the invite is lost with them, so it works best when your friend upgrades while still using their account."
    },
    {
      "type": "h2",
      "text": "If you have a plan change queued"
    },
    {
      "type": "p",
      "text": "If you have already paid for a plan change that starts later, your current plan is extended by 10 days and the queued plan starts 10 days later, still running for its full paid period. Nothing you paid for is lost."
    },
    {
      "type": "h2",
      "text": "Who to invite"
    },
    {
      "type": "p",
      "text": "The program is built for sharing with another Instagram seller. Because the reward only arrives when the person you invite pays for a plan, the best invitations go to sellers who already sell on Instagram and are likely to need a way to take orders in DMs. Ask them to sign up through your link on the browser and device they will keep using, and to do it soon after opening it, so the invite is saved to their account within the 90-day window."
    },
    {
      "type": "h2",
      "text": "Refunds"
    },
    {
      "type": "p",
      "text": "All Checkout DM payments are non-refundable, so a qualified referral is never reversed. You can read the details on the [refund policy](/refund-policy) page. If a referral ever looks wrong, contact Checkout DM. Every referral and reward is recorded, so the team can see exactly what happened."
    },
    {
      "type": "p",
      "text": "If you are still weighing whether the platform suits your own shop, our guide to [the benefits of using Checkout DM](/blog/benefits-of-using-checkout-dm-instagram) is a useful read before you recommend it to someone else."
    }
  ],
  "related": [
    {
      "to": "/referral-guide",
      "title": "Referral Guide",
      "description": "The full rules and common questions for the referral program."
    },
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "Compare plans before you invite someone."
    },
    {
      "to": "/refund-policy",
      "title": "Refund Policy",
      "description": "How payments and refunds work at Checkout DM."
    }
  ],
  "faqs": [
    {
      "q": "When exactly do I get my 10 days?",
      "a": "The moment the merchant you invited pays for their first plan. It is automatic: your expiry date moves forward and you get a notification."
    },
    {
      "q": "Is there a limit on how many people I can refer?",
      "a": "No. Every successful referral adds 10 days, so 3 give you 30 days, 6 give you 60 and so on."
    },
    {
      "q": "Does it matter if I am on a monthly or yearly plan?",
      "a": "No. You always get 10 days per successful referral. The rupee value is higher on a bigger plan because a day of Pro is worth more than a day of Starter."
    },
    {
      "q": "What if my plan expires before my friend pays?",
      "a": "Days are only added to an active paid plan. If your plan has already expired when they pay, that referral does not earn days."
    },
    {
      "q": "Which referrals do not count?",
      "a": "Merchants who already pay or have paid before, sign-ups with your own code or an account using your email, and payments of zero such as a 100%-off coupon."
    }
  ]
};

export default post;
